import React, { useState, useEffect, useCallback } from 'react';

const MERGE_FIELDS = ['customer_name','company','equipment','dates','total','deposit','security_deposit','location_address','governing_state','venue'];
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—';

// Owner-editable, versioned rental terms — what a customer actually signs.
// Separate from ContractManager ("Business Documents"), which is a
// general-purpose upload-and-email tool for other paperwork.
export default function CustomerRentalTerms({ axios, C, card, sec, lbl, inp, locations = [] }) {
  const [allTerms, setAllTerms] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [locationId, setLocationId] = useState(''); // '' = tenant-wide default
  const documentType = 'rental_terms'; // only type in use today; model supports more later

  const [mode, setMode]     = useState('text'); // 'text' | 'upload'
  const [bodyText, setBodyText] = useState('');
  const [title, setTitle]   = useState('Equipment Rental Agreement');
  const [file, setFile]     = useState(null);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/rental-contract-terms');
      setAllTerms(r.data.terms || []);
    } catch(e) {}
    setLoading(false);
  }, [axios]);

  useEffect(() => { load(); }, [load]);

  const scoped = allTerms.filter(t => t.document_type === documentType &&
    (locationId ? t.location_id === Number(locationId) : t.location_id === null));
  const current = scoped.find(t => t.is_current) || null;
  const history = scoped.filter(t => !t.is_current).sort((a,b) => b.version - a.version);

  useEffect(() => {
    if (current?.format === 'text') { setBodyText(current.body_text || ''); setTitle(current.title || 'Equipment Rental Agreement'); }
  }, [current?.id]);

  async function saveText() {
    if (!bodyText.trim()) return;
    setSaving(true); setErr('');
    try {
      await axios.post('/api/rental-contract-terms/text', {
        title, body_text: bodyText, location_id: locationId || null, document_type: documentType,
      });
      await load();
    } catch(e) { setErr(e.response?.data?.error || 'Save failed'); }
    setSaving(false);
  }

  async function uploadPdf() {
    if (!file) return;
    setSaving(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file); fd.append('title', title);
      fd.append('location_id', locationId || ''); fd.append('document_type', documentType);
      await axios.post('/api/rental-contract-terms/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFile(null);
      await load();
    } catch(e) { setErr(e.response?.data?.error || 'Upload failed'); }
    setSaving(false);
  }

  async function clearReview(id) {
    await axios.patch(`/api/rental-contract-terms/${id}/review`, { needs_review: false });
    await load();
  }

  return (
    <div style={card}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
        <div style={sec}>📜 Customer Rental Terms</div>
        {locations.length > 0 && (
          <select style={{ ...inp, width:220 }} value={locationId} onChange={e=>setLocationId(e.target.value)}>
            <option value="">Tenant-wide default</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.name} (override)</option>)}
          </select>
        )}
      </div>

      <div style={{ fontSize:11, color:C.muted, background:'#181928', border:`1px solid ${C.border}`, borderRadius:8, padding:'8px 12px', marginBottom:14 }}>
        This template is a starting point, not legal advice. Have your attorney review your terms.
      </div>

      {loading && <div style={{ color:C.muted, fontSize:12, textAlign:'center', padding:16 }}>Loading…</div>}

      {!loading && current?.needs_review && (
        <div style={{ background:'#3a2a0e', border:'1px solid #92600a', borderRadius:8, padding:'10px 14px', marginBottom:14, fontSize:12, color:'#fbbf24' }}>
          ⚠️ Needs review — {current.review_note || 'not yet confirmed as final.'}
          <button onClick={()=>clearReview(current.id)} style={{ marginLeft:12, padding:'3px 10px', borderRadius:6, border:'1px solid #92600a', background:'transparent', color:'#fbbf24', fontSize:11, cursor:'pointer' }}>Mark reviewed</button>
        </div>
      )}

      {!loading && (
        <>
          <div style={{ display:'flex', gap:8, marginBottom:12 }}>
            <button onClick={()=>setMode('text')} style={{ padding:'6px 14px', borderRadius:7, border:`1px solid ${mode==='text'?C.accent:C.border}`, background: mode==='text'?'rgba(249,115,22,0.12)':'transparent', color: mode==='text'?C.accent:C.muted, fontSize:12, fontWeight:700, cursor:'pointer' }}>Edit Text</button>
            <button onClick={()=>setMode('upload')} style={{ padding:'6px 14px', borderRadius:7, border:`1px solid ${mode==='upload'?C.accent:C.border}`, background: mode==='upload'?'rgba(249,115,22,0.12)':'transparent', color: mode==='upload'?C.accent:C.muted, fontSize:12, fontWeight:700, cursor:'pointer' }}>Upload PDF</button>
            {current && <span style={{ marginLeft:'auto', fontSize:11, color:C.muted, alignSelf:'center' }}>Current: v{current.version} ({current.format})</span>}
          </div>

          <label style={lbl}>Title</label>
          <input style={{ ...inp, marginBottom:10 }} value={title} onChange={e=>setTitle(e.target.value)} />

          {mode === 'text' ? (
            <>
              <label style={lbl}>Wording</label>
              <textarea style={{ ...inp, height:220, resize:'vertical', fontFamily:'monospace', fontSize:12 }} value={bodyText} onChange={e=>setBodyText(e.target.value)} />
              <div style={{ fontSize:11, color:C.muted, marginTop:6, marginBottom:10 }}>
                Merge fields: {MERGE_FIELDS.map(f => <code key={f} style={{ background:'#181928', padding:'1px 5px', borderRadius:4, marginRight:4 }}>{'{{'+f+'}}'}</code>)}
              </div>
              <button disabled={saving || !bodyText.trim()} onClick={saveText}
                style={{ padding:'9px 20px', borderRadius:8, border:'none', background:C.accent, color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer', opacity: saving||!bodyText.trim()?0.5:1 }}>
                {saving ? 'Saving…' : `Save as v${(current?.version||0)+1}`}
              </button>
            </>
          ) : (
            <>
              <label style={lbl}>PDF</label>
              <input type="file" accept="application/pdf" onChange={e=>setFile(e.target.files[0]||null)} style={{ marginBottom:10, fontSize:12, color:C.text }} />
              {current?.format === 'pdf_upload' && <div style={{ fontSize:11, color:C.muted, marginBottom:10 }}>Current file: <a href={current.file_url} target="_blank" rel="noreferrer" style={{ color:C.blue }}>{current.file_name}</a></div>}
              <div><button disabled={saving || !file} onClick={uploadPdf}
                style={{ padding:'9px 20px', borderRadius:8, border:'none', background:C.accent, color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer', opacity: saving||!file?0.5:1 }}>
                {saving ? 'Uploading…' : `Upload as v${(current?.version||0)+1}`}
              </button></div>
            </>
          )}

          {err && <div style={{ color:C.red, fontSize:12, marginTop:10 }}>{err}</div>}

          {history.length > 0 && (
            <div style={{ marginTop:18, paddingTop:14, borderTop:`1px solid ${C.border}` }}>
              <button onClick={()=>setShowHistory(s=>!s)} style={{ background:'none', border:'none', color:C.muted, fontSize:12, cursor:'pointer', padding:0 }}>
                {showHistory ? '▾' : '▸'} Version history ({history.length})
              </button>
              {showHistory && history.map(h => (
                <div key={h.id} style={{ fontSize:11, color:C.muted, padding:'6px 0', borderBottom:`1px solid ${C.border}` }}>
                  v{h.version} · {h.format} · {fmtDate(h.created_at)}{h.needs_review ? ' · needs review' : ''}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
